import { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ConfigBundle } from '@cmp/contracts';

import { ApiError, api, apiBlob, apiWithETag, can } from '../../lib/api';
import type { Draft, Page, PublishResult, Site, Version } from '../../lib/types';
import {
  Badge, Banner, Button, Card, CopyButton, Icon, IconButton, LoadError, Segmented,
  Skeleton, Toast, select as selectCls,
} from '../../lib/ui';
import { Modal, useConfirm } from '../../lib/Modal';
import { nudges } from './guardrails/darkPattern';
import { PropsPanel } from './panels/PropsPanel';
import { TreePanel, type Selection } from './panels/TreePanel';
import { BannerFrame, DEVICES, type Device, type PreviewView, type Zoom } from './preview/BannerFrame';
import { DiffView, diffDrafts } from './publish/DiffView';
import { publishState } from './publish/diff';
import { createDraftStore, setLanguages, type DraftStore } from './store/draftStore';
import { snippet } from '../setup/SetupChecklist';
import { Hilfe } from '../help/Help';
import { fmtDateTime, locale, t, tn } from '../../lib/i18n';

const ISSUE_TEXT: Record<string, string> = {
  reject_required: 'Das Regime verlangt einen sichtbaren „Alle ablehnen“-Button.',
  reject_required_prefs: 'Wenn der Auswahl-Dialog „Alle akzeptieren“ zeigt, muss er auch „Alle ablehnen“ zeigen.',
  unequal_weight: 'Ablehnen und Akzeptieren müssen gleich gestaltet sein.',
  contrast_too_low: 'Der Kontrast liegt unter 4,5:1 (WCAG 2.2 AA).',
  colour_pair_incomplete: 'Fläche und Schrift müssen beide gesetzt sein — sonst lässt sich der Kontrast nicht prüfen.',
  font_invalid: 'Nur ein Schriftname — die Adresse der Schriftdatei gehört in das Feld darunter.',
  font_url_invalid: 'Ein Pfad wie /fonts/brand.woff2 oder eine vollständige https-Adresse.',
  font_incomplete: 'Eine Schrift braucht beides: Familienname und Adresse.',
  required_category: 'Die Kategorie „necessary“ fehlt.',
  reserved: 'Diese Kategorie darf nicht verändert werden.',
  unknown_category: 'Verweist auf eine Kategorie, die es nicht gibt.',
  missing_translation: 'Übersetzung fehlt.',
  duplicate: 'Slug ist doppelt vergeben.',
  pattern: 'Ungültiger Slug.',
  in_use: 'Kategorie wird noch von einem Vendor verwendet.',
};
const issueText = (c: string): string =>
  ISSUE_TEXT[c] ? t(ISSUE_TEXT[c]!)
    : c.startsWith('bundle_too_large') ? t('Bundle über 12 kB gzip.')
    : c;

interface PreviewResp {
  bundle: ConfigBundle;
  bundle_bytes_gz: number;
  issues: { field: string; code: string }[];
}

export function Builder({ site }: { site: Site }) {
  const [store, setStore] = useState<DraftStore | null>(null);

  const loaded = useQuery({
    queryKey: ['draft', site.id],
    queryFn: () => apiWithETag<Draft>(`/v1/sites/${site.id}/config`),
  });

  useEffect(() => {
    if (loaded.data && !store) setStore(() => createDraftStore(loaded.data.data, loaded.data.etag));
  }, [loaded.data, store]);

  if (loaded.isError) {
    // Without this the screen stayed on "wird geladen …" forever: a 403 for a site-scoped
    // colleague and a backend outage both looked like a slow request.
    return (
      <LoadError
        what={t('Der Entwurf')}
        onRetry={() => void loaded.refetch()}
      />
    );
  }
  if (!store) {
    return (
      <div className="space-y-4">
        <div className="h-11 rounded-[4px] border border-[var(--color-line)] bg-[var(--color-surface)]" />
        <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
          <Skeleton rows={10} className="rounded-[4px] border border-[var(--color-line)] bg-[var(--color-surface)] p-3" />
          <div className="h-[520px] rounded-[4px] border border-[var(--color-line)] bg-[var(--color-sunken)]" />
        </div>
      </div>
    );
  }
  return <Inner site={site} store={store} />;
}

function Inner({ site, store }: { site: Site; store: DraftStore }) {
  const qc = useQueryClient();
  const draft = store((s) => s.draft);
  const saved = store((s) => s.saved);
  const dirty = store((s) => s.dirty);
  const saveState = store((s) => s.saveState);
  const lastSavedAt = store((s) => s.lastSavedAt);
  const apply = store((s) => s.apply);

  // Read-only is decided once and shown, rather than being an autosave that quietly does
  // nothing. An analyst used to be able to restructure the whole banner, watch the preview
  // follow along, and lose all of it — the console never said the work was going nowhere.
  const readOnly = !can('config:write');
  const [confirmNode, confirm] = useConfirm();
  const [toast, setToast] = useState<string | null>(null);

  // null is the section list; a value is that section's controls. One state rather than
  // two, because "which section is open" and "is the list showing" are the same question.
  const [selection, setSelection] = useState<Selection | null>(null);
  // The prefs options and category copy are only visible in the second view, so selecting
  // them switches the preview there. Still overridable — the toggle below the frame wins
  // until the next selection.
  const [previewView, setPreviewView] = useState<PreviewView>('banner');
  const select = (s: Selection) => {
    setSelection(s);
    setPreviewView(s.kind === 'prefs' || s.kind === 'category' ? 'preferences' : 'banner');
  };
  const [railOpen, setRailOpen] = useState(true);
  const [zoom, setZoom] = useState<Zoom>('fit');
  const [device, setDevice] = useState<Device>('desktop');

  // Fetched as a blob and turned into a data URL, because the iframe is sandboxed without
  // same-origin and cannot carry the bearer token to fetch it itself.
  const backdrop = useQuery({
    queryKey: ['shot', site.id, device],
    queryFn: async () => {
      const dim = DEVICES[device];
      const blob = await apiBlob(
        `/v1/sites/${site.id}/screenshot?w=${dim.w}&h=${dim.h}`,
      );
      return await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = reject;
        r.readAsDataURL(blob);
      });
    },
    // A site that cannot be rendered is a normal state — a staging domain, a firewall, a
    // page that never finishes. The placeholder is the fallback, not an error to nag about.
    retry: false,
    staleTime: 10 * 60 * 1000,
  });
  const [lang, setLang] = useState(draft.i18n_available[0] ?? 'de');
  const [showPublish, setShowPublish] = useState(false);

  // Debounced so a slider drag does not fire a request per frame.
  const [debounced, setDebounced] = useState<Draft>(draft);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(draft), 250);
    return () => clearTimeout(t);
  }, [draft]);

  const preview = useQuery({
    queryKey: ['preview', site.id, debounced],
    queryFn: () => api<PreviewResp>(`/v1/sites/${site.id}/config/preview`, { method: 'POST', body: debounced }),
    // Without this the iframe unmounted on every debounced edit: the frame collapsed, the
    // byte counter blanked, and the publish button flickered enabled because the issue
    // list momentarily reset to empty. Keeping the previous bundle on screen while the
    // next one is fetched makes the preview feel like a preview.
    placeholderData: keepPreviousData,
  });

  // Same trick as the screenshot, for the same reason: the preview frame is sandboxed
  // with an opaque origin and inherits the console's `img-src 'self' data: blob:`, so the
  // customer's logo can only reach it as bytes we fetched ourselves. A `data:` logo is
  // already inline and needs no help.
  const logoUrl: string = preview.data?.bundle.ui.logo?.url ?? '';
  const logo = useQuery({
    queryKey: ['logo', site.id, logoUrl],
    enabled: logoUrl !== '' && !logoUrl.startsWith('data:'),
    queryFn: async () => {
      const blob = await apiBlob(`/v1/sites/${site.id}/logo?url=${encodeURIComponent(logoUrl)}`);
      return await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = reject;
        r.readAsDataURL(blob);
      });
    },
    // A logo that will not load is a fact about the URL, and the panel's own hints say
    // more about it than a retry loop would.
    retry: false,
    staleTime: 10 * 60 * 1000,
  });

  // ── Autosave (Doc 04 §5.2) ────────────────────────────────────────────────────────
  //
  // Saves are serialised, not raced.
  //
  // The previous version aborted the in-flight request and started a new one — except the
  // AbortController was never handed to fetch, so it only muted the *response*. Both PUTs
  // reached the server carrying the same If-Match, the second got a 412, and the user was
  // shown "changed in parallel" for the crime of continuing to type. The only way out on
  // offer was one unconfirmed click that discarded everything they had written.
  const saving = useRef(false);
  const pending = useRef(false);

  useEffect(() => {
    if (!dirty || readOnly) return;
    if (saving.current) {
      // Note it and let the running save finish; it re-runs afterwards with whatever the
      // draft looks like by then.
      pending.current = true;
      return;
    }

    let cancelled = false;
    saving.current = true;
    store.setState({ saveState: 'saving' });

    void (async () => {
      try {
        await api(`/v1/sites/${site.id}/config`, {
          method: 'PUT',
          body: store.getState().draft,
          headers: store.getState().etag ? { 'If-Match': store.getState().etag! } : {},
        });
        // Re-read for the fresh ETag; without it the next save conflicts with itself.
        const fresh = await apiWithETag<Draft>(`/v1/sites/${site.id}/config`);
        if (!cancelled) store.getState().markSaved(fresh.data, fresh.etag);
      } catch (e) {
        if (!cancelled) {
          store.getState().markFailed(e instanceof ApiError && (e.status === 412 || e.status === 409));
        }
      } finally {
        saving.current = false;
        if (!cancelled && pending.current) {
          pending.current = false;
          // Nudge the effect: the draft moved on while this save was in the air.
          setDebounced(store.getState().draft);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [debounced, dirty, readOnly, site.id, store]);

  const retrySave = () => setDebounced({ ...store.getState().draft });

  // ── Keyboard (Doc 04 §5.2) ────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      // A dialog is open: undo behind it would change what is about to be published while
      // the diff on screen still showed the old thing.
      if (showPublish) return;

      const el = e.target as HTMLElement | null;
      const typing = el?.tagName === 'INPUT' || el?.tagName === 'TEXTAREA' || el?.isContentEditable;
      const k = e.key.toLowerCase();

      // Inside a text field ⌘Z belongs to the field. Hijacking it destroyed native
      // per-character undo and replaced it with "undo the whole command".
      if (typing && (k === 'z' || k === 'y')) return;

      if (k === 'z' && !e.shiftKey) { e.preventDefault(); store.getState().undo(); }
      else if ((k === 'z' && e.shiftKey) || k === 'y') { e.preventDefault(); store.getState().redo(); }
      else if (k === 's') { e.preventDefault(); setDebounced({ ...store.getState().draft }); }
      else if (k === 'enter' && can('config:publish')) { e.preventDefault(); setShowPublish(true); }
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [store, showPublish]);

  // Leaving with unsaved edits should cost a prompt, not the work.
  useEffect(() => {
    const onLeave = (e: BeforeUnloadEvent): void => {
      if (dirty && saveState !== 'saved') e.preventDefault();
    };
    addEventListener('beforeunload', onLeave);
    return () => removeEventListener('beforeunload', onLeave);
  }, [dirty, saveState]);

  const publish = useMutation({
    mutationFn: () => api<PublishResult>(`/v1/sites/${site.id}/config/publish`, { method: 'POST', body: { note: '' } }),
    onSuccess: (r) => {
      setShowPublish(false);
      setToast(t('Veröffentlicht als {version}. Besucher sehen die neue Fassung in wenigen Minuten.',
        { version: r.config_version }));
      void qc.invalidateQueries({ queryKey: ['versions', site.id] });
      void qc.invalidateQueries({ queryKey: ['live', site.id] });
      void qc.invalidateQueries({ queryKey: ['site', site.id] });
      void qc.invalidateQueries({ queryKey: ['sites'] });
    },
  });

  const versions = useQuery({
    queryKey: ['versions', site.id],
    queryFn: () => api<Page<Version>>(`/v1/sites/${site.id}/config/versions`),
  });

  // The diff baseline is what visitors currently get, not the last autosave. With
  // autosave running, "changed since last save" is empty every time it is asked.
  const live = useQuery({
    queryKey: ['live', site.id],
    queryFn: () =>
      api<Draft>(`/v1/sites/${site.id}/config/live`).catch((e) => {
        if (e instanceof ApiError && e.status === 404) return null; // never published
        throw e;
      }),
  });

  // "No live config" and "the live config could not be read" are different facts. Treating
  // the second as the first made the publish dialog announce "first publication" and hide
  // the diff for a site that had been live for months.
  const firstPublish = live.isSuccess && live.data === null;

  const issues = preview.data?.issues ?? [];
  const warnings = useMemo(
    () => nudges(draft, preview.data?.bundle ?? null),
    [draft, preview.data],
  );
  const changes = useMemo(
    () => (live.data ? diffDrafts(live.data, draft) : []),
    [live.data, draft],
  );
  const publishable = issues.length === 0;

  return (
    <div className="space-y-4">
      {confirmNode}
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
      <Toolbar
        site={site}
        store={store}
        saveState={saveState}
        dirty={dirty}
        lastSavedAt={lastSavedAt}
        lang={lang}
        languages={draft.i18n_available}
        onLang={setLang}
        onAddLang={(l) => apply(setLanguages([...draft.i18n_available, l]))}
        device={device}
        onDevice={setDevice}
        publishable={publishable}
        firstPublish={firstPublish}
        pendingChanges={changes.length}
        onPublish={() => setShowPublish(true)}
      />

      {readOnly && (
        <Banner kind="info">
          {t('Sie haben für diese Website nur Lesezugriff. Änderungen hier werden nicht gespeichert — fragen Sie jemanden mit der Rolle „editor“ oder höher.')}
        </Banner>
      )}

      {saveState === 'conflict' && (
        <Banner kind="error">
          <span>
            {t('Diese Konfiguration wurde an anderer Stelle geändert.')}{' '}
            <button className="underline underline-offset-2" onClick={retrySave}>
              {t('Erneut speichern')}
            </button>{' '}
            {t('oder')}{' '}
            <button
              className="underline underline-offset-2"
              onClick={() =>
                confirm({
                  title: t('Serverstand laden?'),
                  danger: true,
                  confirmLabel: t('Serverstand laden'),
                  body: (
                    <>
                      {t('Ihre nicht gespeicherten Änderungen an diesem Banner gehen dabei verloren, einschließlich der Rückgängig-Schritte. Das lässt sich nicht widerrufen.')}
                    </>
                  ),
                  onConfirm: async () => {
                    const fresh = await apiWithETag<Draft>(`/v1/sites/${site.id}/config`);
                    store.getState().takeServer(fresh.data, fresh.etag);
                  },
                })
              }
            >
              {t('den Serverstand laden')}
            </button>
            .
          </span>
        </Banner>
      )}
      {saveState === 'error' && (
        <Banner kind="error">
          <span>
            {t('Automatisches Speichern fehlgeschlagen.')}{' '}
            {/* Without a retry the save stayed stuck: the effect only re-ran when the draft
                changed again, so the fix was "type something else". */}
            <button className="underline underline-offset-2" onClick={retrySave}>
              {t('Erneut versuchen')}
            </button>
          </span>
        </Banner>
      )}

      {issues.length > 0 && (
        <Banner kind="error">
          <strong>{t('So kann nicht veröffentlicht werden:')}</strong>
          <ul className="mt-1 list-disc pl-5">
            {issues.map((i) => (
              <li key={i.field + i.code}><code className="text-xs">{i.field}</code> — {issueText(i.code)}</li>
            ))}
          </ul>
        </Banner>
      )}
      {warnings.length > 0 && (
        <div className="rounded-lg border border-[var(--color-note-line)] bg-[var(--color-note)] px-3 py-2 text-sm text-[var(--color-note-ink)]">
          <ul className="list-disc pl-5">
            {warnings.map((w) => <li key={w.text}>{w.text}</li>)}
          </ul>
        </div>
      )}

      {/* Two columns, not three.
          The list and the properties used to be separate panels on opposite sides of the
          preview, which cost 568px of fixed width to show, at most, eight fields — and
          squeezed the one thing being designed down to 61%. They are the same panel now,
          and it drills down: list, then section, then back. */}
      <div className={`grid gap-4 ${railOpen ? 'lg:grid-cols-[320px_minmax(0,1fr)]' : ''}`}>
        {railOpen && (
          <aside className="self-start rounded-[4px] border border-[var(--color-line)] bg-[var(--color-surface)] p-3">
            {/* A fieldset rather than a prop on every control: disabled cascades, so an
                input added later cannot forget to respect read-only. */}
            <fieldset disabled={readOnly} className="contents">
              {selection === null ? (
                <TreePanel store={store} draft={draft} lang={lang} selection={null} onSelect={select} />
              ) : (
                <div className="space-y-3">
                  <button
                    onClick={() => setSelection(null)}
                    className="-ml-1 inline-flex items-center gap-1 rounded-[3px] px-1 py-0.5 text-xs
                               text-[var(--color-muted)] transition-colors
                               hover:bg-[var(--color-sunken)] hover:text-[var(--color-ink)]"
                  >
                    ← Alle Bereiche
                  </button>
                  <PropsPanel
                    store={store} draft={draft} bundle={preview.data?.bundle ?? null}
                    lang={lang} selection={selection} onSelect={select}
                    // primary_domain first: it is in site_domains too, but a site whose
                    // list has not caught up must not have its own address called foreign.
                    domains={[site.primary_domain, ...(site.domains ?? [])]}
                  />
                </div>
              )}
            </fieldset>
          </aside>
        )}

        <div className="space-y-4">
          <BannerFrame bundle={preview.data?.bundle ?? null} device={device}
            view={previewView} zoom={zoom} backdrop={backdrop.data} logo={logo.data} />
          <div className="flex items-center justify-between text-xs text-[var(--color-muted)]">
            {/* Says which of the two banners this is.
                "echtes SDK-Rendering · auf Ihrer Seite" reads as a window onto the live
                site, so an edit that shows up here and not on the real page looks like a
                broken feature rather than an unpublished one. */}
            <span>
              {changes.length > 0 ? (
                <strong className="font-medium text-[var(--color-note-ink)]">
                  {t('Entwurf — noch nicht veröffentlicht')}
                </strong>
              ) : 'Veröffentlichte Fassung'}
              {' · '}{DEVICES[device].label}
              {backdrop.data
                ? ` · ${t('auf Ihrer Seite')}`
                : backdrop.isPending ? ` · ${t('Seitenbild wird geholt …')}`
                : ` · ${t('Platzhalterseite')}`}
            </span>
            <span className="flex items-center gap-2">
              <Segmented
                size="sm"
                value={previewView}
                onChange={setPreviewView}
                options={[
                  { value: 'banner', label: t('Banner') },
                  { value: 'preferences', label: t('Auswahl-Dialog') },
                ]}
              />
              {/* Fit is the composition; 1:1 is the only way to judge wording and contrast,
                  because at fit-scale the banner's own text is drawn at 8px. */}
              <Segmented
                size="sm"
                value={zoom}
                onChange={setZoom}
                options={[
                  { value: 'fit', label: t('Anpassen') },
                  { value: 'full', label: '100 %' },
                ]}
              />
              <button
                onClick={() => setRailOpen(!railOpen)}
                title={railOpen ? t('Bedienfeld ausblenden') : t('Bedienfeld einblenden')}
                aria-label={railOpen ? t('Bedienfeld ausblenden') : t('Bedienfeld einblenden')}
                className="rounded-[3px] px-1.5 py-1 transition-colors hover:bg-[var(--color-sunken)]
                           hover:text-[var(--color-ink)]"
              >
                {railOpen ? '⤢' : '⤡'}
              </button>
            </span>
            {/* "1930 B gzip von 12 288" means nothing to the person approving the wording.
                The number only matters when it is close to the limit, so that is when it
                speaks up. */}
            <span title={t('Größe der ausgelieferten Konfiguration (Grenze 12 288 B gzip)')}>
              {preview.data
                ? preview.data.bundle_bytes_gz > 10_240
                  ? t('{pct} % des Größenlimits',
                      { pct: Math.round((preview.data.bundle_bytes_gz / 12_288) * 100) })
                  : t('Größe in Ordnung')
                : ''}
            </span>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <InstallCard site={site} />
        <HistoryCard
          site={site}
          versions={versions.data?.data ?? []}
          loading={versions.isPending}
          confirm={confirm}
          onRolledBack={() => {
            void qc.invalidateQueries({ queryKey: ['versions', site.id] });
            void qc.invalidateQueries({ queryKey: ['live', site.id] });
            void qc.invalidateQueries({ queryKey: ['site', site.id] });
            setToast('Zurückgerollt. Besucher sehen wieder diese Fassung.');
          }}
        />
      </div>

      <PublishDialog
          open={showPublish}
          firstPublish={firstPublish}
          liveUnknown={live.isError}
          changes={changes}
          issues={issues.map((i) => `${i.field}: ${issueText(i.code)}`)}
          pending={publish.isPending}
          error={publish.error instanceof ApiError ? publish.error : null}
          onCancel={() => setShowPublish(false)}
          onConfirm={() => publish.mutate()}
        />
    </div>
  );
}

function Toolbar({
  site, store, saveState, dirty, lastSavedAt, lang, languages, onLang, onAddLang,
  device, onDevice, publishable, firstPublish, pendingChanges, onPublish,
}: {
  site: Site; store: DraftStore; saveState: string; dirty: boolean; lastSavedAt: number | null;
  lang: string; languages: string[]; onLang: (l: string) => void; onAddLang: (l: string) => void;
  device: Device; onDevice: (d: Device) => void; publishable: boolean;
  firstPublish: boolean; pendingChanges: number; onPublish: () => void;
}) {
  const canUndo = store((s) => s.past.length > 0);
  const canRedo = store((s) => s.future.length > 0);
  const nextLabel = store((s) => s.past.at(-1)?.label);

  const status =
    saveState === 'saving' ? t('wird gespeichert …')
    : saveState === 'conflict' ? t('Konflikt')
    : saveState === 'error' ? t('Speichern fehlgeschlagen')
    : dirty ? t('ungespeichert')
    : lastSavedAt
      ? t('gespeichert {time}', { time: new Date(lastSavedAt).toLocaleTimeString(locale()) })
      : t('Entwurf');

  // The button is the state, rather than a button standing next to one.
  //
  // Three questions were previously answered in three places: is it saved (a pill), does
  // it differ from what visitors see (a diff inside a dialog you had to open), and may I
  // publish (a button that was simply absent). Now the button says which of those it is.
  //
  // The state machine lives in publish/diff.ts, next to the diff it reads — twice now the
  // rule has been that an empty diff means there is nothing to publish, and twice that
  // has been wrong for a different reason. It is a gate, so it has a test.
  const state = publishState(publishable, firstPublish, pendingChanges);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-[4px] border border-[var(--color-line)] bg-[var(--color-surface)] px-3.5 py-2">
      <div className="flex items-center gap-3">
        <span className="font-semibold">{site.name}</span>
        <span className={`flex items-center text-xs ${dirty ? 'text-[var(--color-note-ink)]' : 'text-[var(--color-muted)]'}`}>
          {dirty ? '●' : '✓'} {status}
          <Hilfe to="entwurf" />
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center">
          <IconButton icon="undo" disabled={!canUndo}
            label={canUndo
              ? t('Rückgängig: {what}', { what: t(nextLabel ?? '') })
              : t('Nichts rückgängig zu machen')}
            onClick={() => store.getState().undo()} />
          <IconButton icon="redo" disabled={!canRedo} label={t('Wiederholen')}
            onClick={() => store.getState().redo()} />
        </div>

        <span className="h-4 w-px bg-[var(--color-line)]" aria-hidden="true" />

        <select className={selectCls}
          aria-label={t('Sprache der Texte')}
          value={lang}
          onChange={(e) => {
            if (e.target.value === '__add') {
              // ponytail: a prompt where a small dialog belongs. It at least says what it
              // rejected now, instead of closing and doing nothing.
              const l = prompt(t('Sprachcode, zwei Buchstaben, z. B. en'))?.trim().toLowerCase();
              if (!l) return;
              if (!/^[a-z]{2}$/.test(l)) {
                alert(t('„{code}“ ist kein zweibuchstabiger Sprachcode. Beispiele: en, fr, it.', { code: l }));
                return;
              }
              if (!languages.includes(l)) { onAddLang(l); onLang(l); }
              return;
            }
            onLang(e.target.value);
          }}>
          {languages.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
          <option value="__add">{t('+ Sprache …')}</option>
        </select>
        <Hilfe to="sprachen" />

        <Segmented
          value={device}
          onChange={onDevice}
          options={(Object.keys(DEVICES) as Device[]).map((d) => ({
            value: d, icon: d, label: DEVICES[d].label,
          }))}
        />

        {/* An unchanged draft still gets a button. It used to get a green "✓ Live" badge,
            which was true about the banner and false about everything else in the bundle:
            a blocking exception lives outside the draft, so the console showed it as
            effective and offered nowhere to publish it. Quiet, not absent. */}
        {can('config:publish') ? (
          <Button
            variant={state === 'ready' || state === 'first' ? 'publish' : 'secondary'}
            onClick={onPublish}
            disabled={state === 'blocked'}
            title={
              state === 'blocked'
                ? t('Es gibt offene Probleme')
                : state === 'first'
                  ? t('Diese Website war noch nie veröffentlicht — Besucher sehen bisher nichts von dieser Konfiguration.')
                  : state === 'unchanged'
                    ? t('Am Banner ändert sich nichts. Regeln, Anbieter und Ausnahmen stehen nicht im Entwurf — erst das Veröffentlichen liefert sie aus. (⌘↵)')
                    : tn('{n} Änderung veröffentlichen (⌘↵)', '{n} Änderungen veröffentlichen (⌘↵)', pendingChanges)
            }
          >
            {state === 'blocked'
              ? t('Nicht veröffentlichbar')
              : state === 'first'
                ? t('Erstmals veröffentlichen')
                : state === 'unchanged'
                  ? t('Erneut veröffentlichen')
                  : t('Veröffentlichen · {n}', { n: pendingChanges })}
          </Button>
        ) : (
          // Absent used to mean "you have no idea why". Present and explained beats gone.
          <span className="text-xs text-[var(--color-muted)]"
            title={t('Bearbeiten dürfen Sie, veröffentlichen dürfen Admin und Owner.')}>
            {t('Veröffentlichen: keine Berechtigung')}
          </span>
        )}
        <Hilfe to="veroeffentlichen" />
      </div>
    </div>
  );
}

function PublishDialog({
  open, firstPublish, liveUnknown, changes, issues, pending, error, onCancel, onConfirm,
}: {
  open: boolean;
  firstPublish: boolean;
  liveUnknown: boolean;
  changes: ReturnType<typeof diffDrafts>;
  issues: string[];
  pending: boolean;
  error: ApiError | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      open={open}
      wide
      title={t('Veröffentlichen')}
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>{t('Abbrechen')}</Button>
          <Button variant="publish" onClick={onConfirm} disabled={pending || issues.length > 0}>
            {pending ? t('Veröffentlichen …') : t('Jetzt veröffentlichen')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-[var(--color-muted)]">
          {t('Danach sehen Besucher diese Fassung. Die vorherige Version bleibt abrufbar.')}
        </p>
        {issues.length > 0 && (
          <Banner kind="error">
            <ul className="list-disc pl-5">{issues.map((i) => <li key={i}>{i}</li>)}</ul>
          </Banner>
        )}
        {liveUnknown ? (
          <Banner kind="error">
            {t('Die aktuell veröffentlichte Fassung konnte nicht geladen werden, deshalb lässt sich hier nicht zeigen, was sich ändert.')}
          </Banner>
        ) : firstPublish ? (
          <Banner kind="info">
            {t('Erste Veröffentlichung. Ab jetzt lädt das SDK diese Konfiguration statt der eingebauten Notfall-Regeln.')}
          </Banner>
        ) : changes.length === 0 ? (
          // "Identisch" on its own reads as "this does nothing", and that reading is what
          // leaves a blocking exception sitting unpublished for weeks.
          <Banner kind="info">
            {t('Am Banner ändert sich nichts. Regeln, Anbieter und Ausnahmen stehen nicht im Entwurf und werden hier nicht verglichen — sie werden beim Veröffentlichen neu gebaut, und genau so kommt eine geänderte Blockier-Regel zu den Besuchern.')}
          </Banner>
        ) : (
          <DiffView changes={changes} />
        )}
        {error && (
          <Banner kind="error">
            {error.status === 403
              ? t('Ihre Rolle darf nicht veröffentlichen. „editor“ darf bearbeiten, veröffentlichen dürfen Admin und Owner.')
              : error.status === 422
                ? t('Die Konfiguration hat noch offene Probleme.')
                : t('Fehlgeschlagen ({status}).', { status: error.status })}
          </Banner>
        )}
      </div>
    </Modal>
  );
}

/**
 * Whether the snippet is actually in, on the actual site.
 *
 * This slot used to hold the snippet itself: a constant string, identical for every site,
 * already shown by the setup wizard and by the checklist, and answering a question you
 * have exactly once. Meanwhile the question that keeps coming back — "is it still
 * installed, and is it early enough?" — had no home at all, because the checklist removes
 * itself once setup is done. Sites change: someone adds a tag manager above the loader,
 * a caching plugin defers scripts, a deploy drops the <head> edit.
 */
function InstallCard({ site }: { site: Site }) {
  const qc = useQueryClient();
  const [showSnippet, setShowSnippet] = useState(false);

  const install = useQuery({
    queryKey: ['install', site.id],
    queryFn: () => api<Install | null>(`/v1/sites/${site.id}/install`),
  });
  const check = useMutation({
    mutationFn: () => api<Install>(`/v1/sites/${site.id}/install`, { method: 'POST' }),
    onSuccess: (r) => qc.setQueryData(['install', site.id], r),
  });
  // The site's own allow rules ride along on the tag. Everything the customer excepted
  // has to survive the seed phase, and the seed phase ends when the config lands — which
  // is after the parser has passed the widget the exception is for.
  const rules = useQuery({
    queryKey: ['rules', site.id],
    queryFn: () => api<{ data: { kind: string; match_type: string; match_value: string }[] }>(
      `/v1/sites/${site.id}/rules`),
  });
  const allow = (rules.data?.data ?? [])
    .filter((r) => r.kind === 'allow' && r.match_type === 'domain' && r.match_value !== 'self')
    .map((r) => r.match_value);

  const i = install.data;
  const fired = i?.trackers_before_consent ?? [];
  const ok = i?.loader_found === true && i.site_key_matches && fired.length === 0;

  return (
    <Card
      title={t('Einbau')}
      count={undefined}
      actions={
        <span className="flex items-center gap-1">
          <Button variant="secondary" size="sm" disabled={check.isPending}
            onClick={() => check.mutate()}>
            {check.isPending ? t('Wird geprüft …') : t('Jetzt prüfen')}
          </Button>
          <Hilfe to="snippet" />
        </span>
      }
    >
      {install.isPending ? (
        <Skeleton rows={2} />
      ) : !i ? (
        <p className="text-[13px] text-[var(--color-muted)]">
          {t('Noch nicht geprüft. Der Test lädt {domain} und sieht nach, ob der Loader da ist und ob vor der Einwilligung etwas feuert.', { domain: site.primary_domain })}
        </p>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {i.error ? <Badge tone="note">{t('Nicht prüfbar')}</Badge>
              : ok ? <Badge tone="live"><Icon name="check" size={12} /> {t('Eingebaut')}</Badge>
              : <Badge tone="danger">{t('Nicht in Ordnung')}</Badge>}
            <span className="text-xs text-[var(--color-muted)]">
              {t('geprüft {when}', { when: fmtDateTime(i.checked_at) })}
            </span>
          </div>
          {/* When the page could not be loaded, everything downstream is unknown rather
              than failed. Reporting "kein Snippet gefunden" because we never looked is the
              same error as rendering a failed request as an empty result — it accuses the
              customer of a problem we did not observe. */}
          <ul className="space-y-1 text-[13px]">
            <Check state={i.error ? 'bad' : 'ok'} label={t('Seite erreichbar')} detail={i.error ?? undefined} />
            <Check state={i.error ? 'unknown' : i.loader_found ? 'ok' : 'bad'} label={t('Loader gefunden')}
              detail={i.error ? t('Nicht geprüft — die Seite war nicht erreichbar.')
                : i.loader_found ? undefined : t('Auf der Startseite steht kein Snippet.')} />
            <Check state={i.error || !i.loader_found ? 'unknown' : i.site_key_matches ? 'ok' : 'bad'}
              label={t('Richtiger Site-Schlüssel')}
              detail={i.error || !i.loader_found ? t('Nicht geprüft.')
                : i.site_key_matches ? undefined : t('Es lädt ein Loader einer anderen Website.')} />
            {/* Installed and still too late is the case worth naming: the snippet is
                present, the banner shows, and vendors have already fired above it. */}
            <Check state={i.error ? 'unknown' : fired.length === 0 ? 'ok' : 'bad'}
              label={t('Nichts vor der Einwilligung')}
              detail={i.error ? t('Nicht geprüft.') : fired.length === 0 ? undefined
                : t('{vendors} — das Snippet steht zu weit unten im <head>.', { vendors: fired.join(', ') })} />
          </ul>
        </div>
      )}

      <div className="mt-4 border-t border-[var(--color-line)] pt-3">
        <div className="flex items-center justify-between gap-3">
          <button className="text-xs text-[var(--color-brand)] hover:underline"
            onClick={() => setShowSnippet(!showSnippet)}>
            {showSnippet ? t('Snippet ausblenden') : t('Snippet anzeigen')}
          </button>
          {showSnippet && <CopyButton value={snippet(site.site_key, allow)} />}
        </div>
        {showSnippet && (
          <pre className="mt-2 overflow-x-auto rounded-[3px] border border-[var(--color-line)] bg-[var(--color-paper)] p-3 text-xs leading-relaxed">
{snippet(site.site_key, allow)}
          </pre>
        )}
      </div>
    </Card>
  );
}

interface Install {
  checked_at: string;
  loader_found: boolean;
  site_key_matches: boolean;
  banner_rendered: boolean;
  /** Never null from this server any more, still optional so an older one cannot crash it. */
  trackers_before_consent?: string[] | null;
  error: string | null;
}

function Check({ state, label, detail }: {
  state: 'ok' | 'bad' | 'unknown'; label: string; detail?: string;
}) {
  const mark = { ok: '✓', bad: '✕', unknown: '–' }[state];
  const tone = {
    ok: 'text-[var(--color-live)]',
    bad: 'text-[var(--color-danger)]',
    unknown: 'text-[var(--color-muted)]',
  }[state];
  return (
    <li className="flex gap-2">
      <span aria-hidden="true" className={tone}>{mark}</span>
      <span className="min-w-0">
        {label}
        {detail && <span className="block text-xs text-[var(--color-muted)]">{detail}</span>}
      </span>
    </li>
  );
}

/**
 * What was published, by whom, and the way back.
 *
 * Ten rows of date and ULID answered nothing. The questions people actually bring to a
 * version list are "who changed the consent notice", "what did it say", and — the urgent
 * one from Doc 05 §12.3 — "put the previous one back". That last one had no button at all;
 * the runbook's answer was a shell command, on the path where minutes matter because a
 * wrong config may be releasing tracking nobody consented to.
 */
function HistoryCard({ site, versions, loading, confirm, onRolledBack }: {
  site: Site;
  versions: Version[];
  loading: boolean;
  confirm: ReturnType<typeof useConfirm>[1];
  onRolledBack: () => void;
}) {
  const [problem, setProblem] = useState<string | null>(null);
  const roll = useMutation({
    mutationFn: (cv: string) => api(`/v1/sites/${site.id}/config/rollback`,
      { method: 'POST', body: { config_version: cv } }),
    onSuccess: () => { setProblem(null); onRolledBack(); },
    onError: (e) => setProblem(
      e instanceof ApiError && e.status === 403
        ? t('Ihre Rolle darf nicht veröffentlichen und damit auch nicht zurückrollen.')
        : t('Zurückrollen fehlgeschlagen.'),
    ),
  });

  return (
    <Card title={t('Verlauf')} count={versions.length || undefined}>
      {problem && <div className="mb-3"><Banner kind="error">{problem}</Banner></div>}
      {loading ? <Skeleton rows={3} />
        : versions.length === 0 ? (
          <p className="text-[13px] text-[var(--color-muted)]">{t('Noch nichts veröffentlicht.')}</p>
        ) : (
          <ul className="divide-y divide-[var(--color-line)]">
            {versions.map((v) => (
              <li key={v.config_version} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 py-2.5">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-2 text-[13px]">
                    <span>
                      {new Date(v.published_at).toLocaleString(locale(), {
                        day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                    {v.published_by && (
                      <span className="text-xs text-[var(--color-muted)]">{t('von {who}', { who: v.published_by })}</span>
                    )}
                    {v.is_live && <Badge tone="live">{t('live')}</Badge>}
                  </div>
                  {v.title && (
                    <div className="mt-0.5 truncate text-xs text-[var(--color-muted)]">„{v.title}“</div>
                  )}
                  {/* The id stays: it is what a support request and a consent proof quote. */}
                  <div className="truncate font-mono text-[10px] text-[var(--color-muted)]/70"
                    title={t('Textprüfsumme {hash}', { hash: v.text_hash })}>
                    {v.config_version}
                  </div>
                </div>
                {!v.is_live && can('config:publish') && (
                  <button
                    className="shrink-0 text-xs text-[var(--color-brand)] hover:underline"
                    onClick={() => confirm({
                      title: t('Diese Fassung wieder live schalten?'),
                      confirmLabel: t('Zurückrollen'),
                      body: (
                        <>
                          {t('Besucher sehen ab sofort wieder die Fassung vom {when}. Ihr Entwurf bleibt unverändert — der Editor bietet danach weiter an, ihn zu veröffentlichen.',
                            { when: fmtDateTime(v.published_at) })}
                        </>
                      ),
                      onConfirm: () => roll.mutate(v.config_version),
                    })}>
                    {t('Zurückrollen')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
    </Card>
  );
}
